import { useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { formatNumber } from "src/utils/numberFormat";
import type {
  BrandSummary,
  DriverRow,
  InsightCard,
  LeadNote,
  ThemeComparisonRow,
  ThemeScore,
} from "../Types/ListeningReportTypes";
import {
  formatSignedNumber,
  getDriverNet,
  getDriverStatus,
  getSignedTone,
  pluralize,
  sortDrivers,
  isDriverSortKey,
  type DriverSortKey,
  type DriverStatus,
} from "../Utils/ListeningReportUtils";
import { CardTitle, REPORT_KEY, ReportBadge, ReportTag, SectionTitle, getBrandScope } from "./ReportPrimitives";

const DRIVERS_KEY = `${REPORT_KEY}.drivers`;
const MAX_DRIVER_BAR_VALUE = 40;
const STATUS_TONE: Record<DriverStatus, "pos" | "neg" | "neu"> = {
  strength: "pos",
  watch: "neu",
  risk: "neg",
};

const barWidth = (value: number) => `${(value / MAX_DRIVER_BAR_VALUE) * 100}%`;

const DriverBars: FC<{ driver: DriverRow }> = ({ driver }) => {
  const { t } = useTranslation();
  return (
    <div
      className="br__dv"
      role="img"
      aria-label={t(`${DRIVERS_KEY}.barLabel`, "{{negative}} negative, {{positive}} positive", {
        negative: driver.negative,
        positive: driver.positive,
      })}
    >
      <em className={`br__dv-neg${driver.negative ? "" : " br__dv-zero"}`}>{driver.negative}</em>
      <span className="br__dv-track br__dv-track--l">
        <span className="br__dv-bar" style={{ width: barWidth(driver.negative) }} />
      </span>
      <span className="br__dv-track br__dv-track--r">
        <span className="br__dv-bar" style={{ width: barWidth(driver.positive) }} />
      </span>
      <em className={`br__dv-pos${driver.positive ? "" : " br__dv-zero"}`}>{driver.positive}</em>
    </div>
  );
};

const ThemeScoreCell: FC<{ score: ThemeScore; items: number }> = ({ score, items }) => {
  const { t } = useTranslation();
  const tone = getSignedTone(score.net);
  return (
    <td className="br__num">
      <span className={`br__net${tone ? ` br__net--${tone}` : ""}`}>{formatSignedNumber(score.net, 1)}</span>
      <span className="br__td-sub">
        {t(
          `${DRIVERS_KEY}.scoreDetail`,
          "{{praise}}, {{complaints}} · {{mentions}} in {{items}}",
          {
            praise: pluralize(score.praise, "praise", "praise"),
            complaints: pluralize(score.complaints, "complaint"),
            mentions: pluralize(score.mentions, "mention"),
            items: pluralize(items, "item"),
          }
        )}
      </span>
    </td>
  );
};

interface DriversSectionProps {
  primary: BrandSummary;
  competitor?: BrandSummary;
  drivers: DriverRow[];
  insights: InsightCard[];
  themeComparison: ThemeComparisonRow[];
  wins: LeadNote[];
}

const DriversSection: FC<DriversSectionProps> = ({
  primary,
  competitor,
  drivers,
  insights,
  themeComparison,
  wins,
}) => {
  const { t, i18n } = useTranslation();
  const [sortKey, setSortKey] = useState<DriverSortKey>("neg");
  const sortedDrivers = useMemo(() => sortDrivers(drivers, sortKey), [drivers, sortKey]);
  const sortOptions = useMemo(
    () => [
      { value: "neg", label: t(`${DRIVERS_KEY}.sortComplaints`, "Most complaints") },
      { value: "pos", label: t(`${DRIVERS_KEY}.sortPraise`, "Most praise") },
      { value: "net", label: t(`${DRIVERS_KEY}.sortNet`, "Lowest net score") },
    ],
    [t]
  );
  const names = { primary: primary.name, competitor: competitor?.name ?? "" };
  const sampleRatio = competitor?.scoredItems
    ? (primary.scoredItems / competitor.scoredItems).toFixed(1)
    : "–";
  const formatCount = (value: number) => formatNumber(value, {}, i18n.language);

  const statusLabels: Record<DriverStatus, string> = {
    strength: t(`${DRIVERS_KEY}.statusStrength`, "Strength"),
    watch: t(`${DRIVERS_KEY}.statusWatch`, "Watch"),
    risk: t(`${DRIVERS_KEY}.statusRisk`, "Risk"),
  };

  return (
    <section className="br__panel" id="br-drivers" data-pdf-block="br-drivers" data-pdf-splittable aria-label={t(`${REPORT_KEY}.tabs.drivers`, "Drivers")}>
      <SectionTitle scope={getBrandScope(t, primary.name, competitor?.name)}>
        {t(`${REPORT_KEY}.tabs.drivers`, "Drivers")}
      </SectionTitle>

      <div className="br__grid">
        <div
          className={`br__card br__card--flush ${insights.length > 0 ? "br__c8" : "br__c12"}`}
        >
          <div className="br__card-top">
            <div>
              <CardTitle
                help={t(`${DRIVERS_KEY}.help`, "Negative and positive mentions per theme, on the same scale")}
              >
                {t(`${DRIVERS_KEY}.title`, "{{primary}} Sentiment Drivers", names)}
              </CardTitle>
              <p className="br__card-desc">
                {t(
                  `${DRIVERS_KEY}.description`,
                  "Complaints on the left, praise on the right. Net is praise minus complaints."
                )}
              </p>
            </div>
            <div className="br__dd">
              <StyledDropdown
                value={sortKey}
                options={sortOptions}
                onChange={value => isDriverSortKey(value) && setSortKey(value)}
                aria-label={t(`${DRIVERS_KEY}.sortLabel`, "Sort themes")}
              />
            </div>
          </div>
          <div className="br__table-wrap">
            <table className="br__table br__table--drv">
              <colgroup>
                <col />
                <col style={{ width: 108 }} />
                <col style={{ width: 330 }} />
                <col style={{ width: 84 }} />
                <col style={{ width: 84 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t(`${DRIVERS_KEY}.theme`, "Theme")}</th>
                  <th>{t(`${DRIVERS_KEY}.status`, "Status")}</th>
                  <th>
                    <div className="br__dv br__dv--head">
                      <span />
                      <span className="br__dv-neg-head">{t(`${DRIVERS_KEY}.negative`, "Negative")}</span>
                      <span className="br__dv-pos-head">{t(`${DRIVERS_KEY}.positive`, "Positive")}</span>
                      <span />
                    </div>
                  </th>
                  <th className="br__ctr">{t(`${DRIVERS_KEY}.neutral`, "Neutral")}</th>
                  <th className="br__ctr">{t(`${DRIVERS_KEY}.net`, "Net")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedDrivers.map(driver => {
                  const net = getDriverNet(driver);
                  const status = getDriverStatus(driver);
                  const tone = getSignedTone(net);
                  return (
                    <tr key={driver.name}>
                      <td>
                        <b>{driver.name}</b>
                        <span className="br__td-sub">{driver.note}</span>
                      </td>
                      <td>
                        {status ? (
                          <ReportBadge tone={STATUS_TONE[status]}>{statusLabels[status]}</ReportBadge>
                        ) : (
                          "–"
                        )}
                      </td>
                      <td>
                        <DriverBars driver={driver} />
                      </td>
                      <td className="br__ctr">{formatCount(driver.neutral)}</td>
                      <td className="br__ctr">
                        <span className={`br__net${tone ? ` br__net--${tone}` : ""}`}>
                          {formatSignedNumber(net)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {insights.length > 0 && (
        <div className="br__c4 br__insights">
          {insights.map(insight => (
            <div className="br__card" key={insight.title}>
              <h3 className="br__card-title br__ins-head">
                {insight.title}
                <ReportTag small tone={insight.tagTone}>
                  {insight.tag}
                </ReportTag>
              </h3>
              <p>{insight.body}</p>
            </div>
          ))}
        </div>
        )}
      </div>

      <div className="br__grid">
        {competitor && (
        <div className="br__card br__card--flush br__c12">
          <div className="br__card-top">
            <div>
              <CardTitle
                help={t(
                  `${DRIVERS_KEY}.comparisonHelp`,
                  "Net mentions (praise minus complaints) per 100 scored items, so brands of different sample sizes can be compared"
                )}
              >
                {t(`${DRIVERS_KEY}.comparisonTitle`, "Theme Comparison: {{primary}} vs {{competitor}}", names)}
              </CardTitle>
              <p className="br__card-desc">
                {t(
                  `${DRIVERS_KEY}.comparisonDescription`,
                  "Scores are net mentions per 100 scored items, so the two brands can be compared fairly."
                )}
              </p>
            </div>
          </div>
          <div className="br__note-box br__note-box--sample">
            <b>{t(`${DRIVERS_KEY}.sampleLead`, "Sample sizes differ:")}</b>{" "}
            {t(
              `${DRIVERS_KEY}.sampleBody`,
              "{{primary}} has {{primaryItems}} scored items and {{competitor}} {{competitorItems}} ({{primary}} has {{ratio}}× as many). Raw counts would favour {{primary}}, so each score is scaled per 100 items. Themes where either brand has fewer than 10 mentions are flagged as low sample; treat their leader as a hint, not a result.",
              {
                ...names,
                primaryItems: formatCount(primary.scoredItems),
                competitorItems: formatCount(competitor.scoredItems),
                ratio: sampleRatio,
              }
            )}
          </div>
          <div className="br__table-wrap br__table-wrap--cmp">
            <table className="br__table br__table--cmp">
              <thead>
                <tr>
                  <th>{t(`${DRIVERS_KEY}.theme`, "Theme")}</th>
                  <th className="br__num">
                    {primary.name}
                    <span className="br__th-n">
                      {t(`${DRIVERS_KEY}.perHundred`, "per 100 items · n={{count}}", {
                        count: formatCount(primary.scoredItems),
                      })}
                    </span>
                  </th>
                  <th className="br__num">
                    {competitor.name}
                    <span className="br__th-n">
                      {t(`${DRIVERS_KEY}.perHundred`, "per 100 items · n={{count}}", {
                        count: formatCount(competitor.scoredItems),
                      })}
                    </span>
                  </th>
                  <th>{t(`${DRIVERS_KEY}.leader`, "Leader")}</th>
                  <th>{t(`${DRIVERS_KEY}.confidence`, "Confidence")}</th>
                </tr>
              </thead>
              <tbody>
                {themeComparison.map(row => (
                  <tr key={row.theme}>
                    <td>{row.theme}</td>
                    <ThemeScoreCell score={row.primary} items={primary.scoredItems} />
                    <ThemeScoreCell score={row.competitor} items={competitor.scoredItems} />
                    <td>
                      <ReportBadge tone="neu">{row.leader}</ReportBadge>
                    </td>
                    <td>
                      <ReportTag small tone={row.lowSample ? "neg" : "pos"}>
                        {row.lowSample
                          ? t(`${DRIVERS_KEY}.lowSample`, "Low sample")
                          : t(`${DRIVERS_KEY}.enoughData`, "Enough data")}
                      </ReportTag>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        <div className="br__card br__wins br__c12">
          <div className="br__card-top">
            <CardTitle help={t(`${DRIVERS_KEY}.winsHelp`, "Areas where {{primary}} performs well in this run", names)}>
              {t(`${DRIVERS_KEY}.winsTitle`, "{{primary}} strengths", names)}
            </CardTitle>
            <ReportBadge tone="pos">{t(`${DRIVERS_KEY}.statusStrength`, "Strength")}</ReportBadge>
          </div>
          {wins.map(win => (
            <div className="br__win" key={win.lead}>
              <p>
                <b>{win.lead}</b> {win.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DriversSection;
